import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';

import CustomerLayout from '../../CustomerLayout';
import { getCustomerCompanyJobs } from "../../../../../services/staffingService";
import ListView from './ListView';
import GridView from './GridView';
import CalendarView from './CalendarView';
import NewWorkOrderSlideOver from '../NewWorkOrderSlideOver';
import moment from 'moment';
import { Briefcase, List, LayoutGrid, Calendar, Filter, Loader2 } from 'lucide-react';

// ─── status config ────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  open:      'bg-blue-100 text-blue-700',
  expired:   'bg-gray-100 text-gray-500',
  cancelled: 'bg-red-100 text-red-600',
  completed: 'bg-emerald-100 text-emerald-700',
  paid:      'bg-teal-100 text-teal-700',
  disputed:  'bg-orange-100 text-orange-700',
  resolved:  'bg-purple-100 text-purpleald-700',
};

const STATUS_OPTIONS = ['all', 'open', 'completed', 'disputed', 'cancelled', 'expired', 'paid', 'resolved'];

// ─── helpers ──────────────────────────────────────────────────────────────────
function getDeadlineText(shifts) {
  if (!shifts || shifts.length === 0) return 'No shifts';
  const firstShift = shifts.reduce((earliest, s) =>
    new Date(s.startTime) < new Date(earliest.startTime) ? s : earliest
  );
  const now = moment();
  const deadline = moment(firstShift.startTime).subtract(12, 'hours');
  const dur = moment.duration(deadline.diff(now));
  if (dur.asDays() >= 1) return `${Math.floor(dur.asDays())}d left`;
  if (dur.asHours() >= 1) return `${Math.floor(dur.asHours())}h left`;
  if (dur.asMinutes() > 0) return `${Math.floor(dur.asMinutes())}m left`;
  return 'Expired';
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── CoverageBar ──────────────────────────────────────────────────────────────
function CoverageBar({ pct }) {
  const color = pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs text-gray-500">{Math.round(pct)}%</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const WorkOrdersPage = () => {
  const { customerCompanyId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [view, setView] = useState('calendar'); // default to weekly shift calendar
  const [slideOverOpen, setSlideOverOpen] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (selectedStatus === 'all') {
      setFilteredJobs(jobs);
    } else {
      setFilteredJobs(jobs.filter((j) => j.status === selectedStatus));
    }
  }, [selectedStatus, jobs]);

  const fetchJobs = async () => {
    setLoading(true);
    const resp = await getCustomerCompanyJobs(customerCompanyId);
    const allJobs = resp.data || [];
    setJobs(allJobs);
    setFilteredJobs(allJobs);
    setLoading(false);
  };

  const getJobCountByStatus = (s) =>
    s === 'all' ? jobs.length : jobs.filter((j) => j.status === s).length;

  return (
    <>
    <CustomerLayout>
      <main className="flex-1 overflow-y-auto">
        <div className="p-6">
          {/* ── Page header ─────────────────────────────────── */}
          <div className="mb-6 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#404880]/10">
                <Briefcase size={20} className="text-[#404880]" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Work Orders</h1>
                <p className="text-sm text-gray-500">
                  {jobs.length > 0 ? `${jobs.length} total` : 'Manage security requests'}
                </p>
              </div>
            </div>

            {/* All controls share the same h-8 height */}
            <div className="flex items-center gap-2">
              {/* New Work Order */}
              <button
                onClick={() => setSlideOverOpen(true)}
                className="flex h-8 items-center gap-1.5 rounded-lg bg-[#CC457D] px-3.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
              >
                + New Work Order
              </button>

              {/* List / Grid / Calendar toggle */}
              <div className="flex h-8 overflow-hidden rounded-lg border border-gray-200">
                {[{key:'list',Icon:List,label:'List'},{key:'grid',Icon:LayoutGrid,label:'Grid'},{key:'calendar',Icon:Calendar,label:'Calendar'}].map(({key,Icon,label},i) => (
                  <button
                    key={key}
                    onClick={() => setView(key)}
                    className={`flex h-full items-center gap-1.5 px-3 text-xs font-medium transition-colors ${
                      i > 0 ? 'border-l border-gray-200' : ''
                    } ${view === key ? 'bg-[#404880] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    <Icon size={13} />{label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Filter bar (list / grid view) ───────────────── */}
          {(view === 'list' || view === 'grid') && jobs.length > 0 && (
            <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3">
              <Filter size={14} className="text-gray-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-500">Status</span>
              <div className="flex flex-wrap gap-1.5">
                {STATUS_OPTIONS.filter((s) => getJobCountByStatus(s) > 0 || s === 'all').map((s) => (
                  <button
                    key={s}
                    onClick={() => setSelectedStatus(s)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      selectedStatus === s
                        ? 'bg-[#404880] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                    <span className="ml-1 opacity-70">({getJobCountByStatus(s)})</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Loading ──────────────────────────────────────── */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-[#404880]" />
            </div>
          )}

         

          {/* ── List view ────────────────────────────────────── */}
          {!loading && jobs.length > 0 && view === 'list' && (
            <>
              {filteredJobs.length > 0 ? (
                <ListView data={filteredJobs} getDeadlineText={getDeadlineText} />
              ) : (
                <div className="py-12 text-center text-sm text-gray-500">
                  No work orders with status &ldquo;{selectedStatus}&rdquo;
                </div>
              )}
            </>
          )}

          {/* ── Grid view ────────────────────────────────────── */}
          {!loading && jobs.length > 0 && view === 'grid' && (
            <>
              {filteredJobs.length > 0 ? (
                <GridView data={filteredJobs} getDeadlineText={getDeadlineText} />
              ) : (
                <div className="py-12 text-center text-sm text-gray-500">
                  No work orders with status &ldquo;{selectedStatus}&rdquo;
                </div>
              )}
            </>
          )}

          {/* ── Calendar view ─────────────────────────────────── */}
          {view === 'calendar' && (
            <CalendarView
              onShiftClick={(shift) =>
                history.push(`/customer-company/${customerCompanyId}/jobs/${shift.job_id}/shift/${shift._id}`)
              }
            />
          )}
        </div>
      </main>
    </CustomerLayout>

    {/* New Work Order slide-over */}
    <NewWorkOrderSlideOver
      open={slideOverOpen}
      onClose={() => setSlideOverOpen(false)}
      onCreated={fetchJobs}
    />
    </>
  );
};

export default WorkOrdersPage;

